/**
 * "Rename folder" against the mail server itself.
 *
 * A rename, not a move: there is no parent picker here on purpose. The two
 * questions look alike in a dialog and are not alike at all on the server - a
 * rename that also relocated a folder because a path was mistyped would move
 * every message under it, and the operator asked to change a word.
 *
 * The same one-segment rule as creating applies, and for the same reason: the
 * character that separates a path is the server's choice, so a typed separator
 * would nest on one provider and become a literal name on the next.
 */
import { useState } from "react";
import { Loader2, PenLine } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import { renameImapFolder, type ImapFolder } from "../../../../lib/mail";
import { Btn, Modal } from "../../../ui";
import { imapFolderError } from "./imapFolderErrors";

export function RenameImapFolderModal({
  mailbox,
  folder,
  onClose,
  onRenamed,
}: {
  /** The mailbox the folder is in: "shared" or an own mailbox id. */
  mailbox: string;
  folder: ImapFolder;
  onClose: () => void;
  onRenamed: (path: string) => void;
}) {
  const t = useT();
  const [name, setName] = useState(folder.name);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const unchanged = name.trim() === folder.name;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await renameImapFolder({ path: folder.path, name: name.trim(), mailbox });
      onRenamed(res.path);
      onClose();
    } catch (err) {
      setError(imapFolderError(t, err, "email.imap.renameFailed"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={t("email.imap.renameTitle")}
      label={t("email.imap.renameTitle")}
      onClose={onClose}
      className="max-w-[440px]"
    >
      <form onSubmit={submit} className="mt-1 flex flex-col gap-3">
        <div>
          <span className="crm-lab">{t("email.imap.nameField")} *</span>
          <label className="crm-inp">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="off"
              autoFocus
              maxLength={64}
              required
            />
          </label>
          {/* Its own hint: the shared one ends in "pick the location below",
              and there is no location picker here. */}
          <span className="crm-sub !mt-1.5 block text-[11.5px] leading-snug">
            {t("email.imap.nameHintRename")}
          </span>
        </div>

        {/* Where it sits is stated rather than offered. A folder deep in a tree
            is renamed by its last segment, and the full path is the only thing
            that says which of two "2026" folders is on the table. */}
        <p className="crm-sub text-[11.5px] leading-snug">{t("email.imap.renameFrom", { path: folder.path })}</p>

        {error ? <p className="text-[12.5px] leading-snug text-destructive">{error}</p> : null}

        <div className="crm-m-foot">
          <Btn type="button" onClick={onClose} disabled={busy}>
            {t("common.cancel")}
          </Btn>
          <Btn type="submit" tone="dark" disabled={busy || !name.trim() || unchanged}>
            {busy ? <Loader2 className="size-3.5 animate-spin" /> : <PenLine className="size-3.5" />}
            {t("email.imap.rename")}
          </Btn>
        </div>
      </form>
    </Modal>
  );
}
